import { CartLinesUpdateEvent } from '@shopify/events';

// Preserve every existing property: Shopify replaces the whole properties object.
class CartItemNote extends HTMLElement {
  connectedCallback() {
    // The customizer adds Edit Design asynchronously; keep it above Add Note.
    this.editObserver?.disconnect();
    const placeEditLink = () => {
      const link = this.parentElement?.querySelector('[data-customizer-cart-edit], .customizer-cart-edit');
      if (link && link.parentElement !== this) this.prepend(link);
    };
    placeEditLink();
    this.editObserver = new MutationObserver(placeEditLink);
    this.editObserver.observe(this.parentElement, { childList: true, subtree: true });
    if (this.initialized) return;
    this.initialized = true;
    this.querySelector('[data-save-note]').addEventListener('click', () => this.save());
  }

  disconnectedCallback() {
    this.editObserver?.disconnect();
  }

  async save() {
    const button = this.querySelector('[data-save-note]');
    const textarea = this.querySelector('textarea');
    const status = this.querySelector('[role="status"]');
    if (button.disabled) return;
    button.disabled = true;
    textarea.disabled = true;
    status.textContent = 'Saving note…';
    try {
      const root = window.Shopify?.routes?.root || '/';
      const current = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!current.ok) throw new Error('Could not read cart');
      const cart = await current.json();
      const item = cart.items.find((line) => line.key === this.dataset.key);
      if (!item) throw new Error('Item changed');
      const note = textarea.value.trim();
      // An unchanged note is already saved. Give the same visible completion
      // without asking Shopify to replace an identical line and morph the editor.
      if (note === (item.properties?.['Special instructions'] || '')) {
        this.querySelector('details').open = false;
        status.textContent = 'Note saved.';
        return;
      }
      const properties = { ...item.properties, 'Special instructions': textarea.value.trim() };
      const sections = [...new Set([...document.querySelectorAll('cart-items-component')]
        .map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key, properties, sections, sections_url: window.location.pathname }),
      });
      const updated = await response.json();
      if (!response.ok || updated.errors) throw new Error('Could not save note');
      status.textContent = 'Note saved.';
      // Refresh both cart surfaces, including the new line key assigned by Shopify.
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'update', context: 'cart', lines: [{ id: item.key, quantity: item.quantity }],
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updated),
          detail: { sections: updated.sections, items: updated.items, itemCount: updated.item_count,
            source: 'km-cart-item-note', didError: false },
        }),
      }));
    } catch (error) {
      status.textContent = 'Your note could not be saved. Please try again. If this item changed, refresh your cart first.';
    } finally {
      button.disabled = false;
      textarea.disabled = false;
    }
  }
}

if (!customElements.get('km-cart-item-note')) customElements.define('km-cart-item-note', CartItemNote);
